import { Stack } from 'component-library';
import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { TrendLabel as TrendLabelEnum } from '@/api/rest/resources/conservation';
import { ChartLegendBar } from '@/components/Charts';
import { CustomActiveBar } from '@/components/Charts/components/CustomActiveBar';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { ChartProps } from '@/components/Charts/types';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import {
  Bar,
  Chart,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
  ComposedChart,
  TooltipProps,
  useTimeseriesXAxisProps,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/Chart';
import {
  RenderTrendLine,
  TrendLabel,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/ReferenceLine';
import { printYear } from '@/utils/formatting/date';

const VEGETATION_LABEL = 'shared.ncaDetail.details.landMonitoring.ndvi.vegetation';

const chartColors = {
  ndvi: {
    bar: {
      fill: '#65D365',
    },
    line: {
      stroke: '#51A687',
      dot: '#474D45',
    },
  },
} as const;

export type NdviChartData = {
  ndviBar: number;
  ndviLine: number;
  date: Date;
};

type NdviChart = HTMLAttributes<HTMLDivElement> & {
  data: NdviChartData[];
  trendLabel: TrendLabelEnum | null;
} & Omit<ChartProps, 'data'>;

export const NDVIHistoricalChart: FC<NdviChart> = ({ data, trendLabel, ...delegated }) => {
  const { t } = useTranslation();

  const xAxisProps = useTimeseriesXAxisProps(data);

  return (
    <>
      <Chart.Container
        {...delegated}
        data={data}
        config={{
          ndviBar: {
            label: t(VEGETATION_LABEL),
            color: chartColors.ndvi.bar.fill,
          },
          ndviLine: {
            label: t(VEGETATION_LABEL),
            color: chartColors.ndvi.line.stroke,
          },
        }}
        data-testid='ndvi-chart'
      >
        <ComposedChart data={data}>
          <Bar
            isAnimationActive={false}
            fill={chartColors.ndvi.bar.fill}
            dataKey='ndviBar'
            activeBar={<CustomActiveBar fill={chartColors.ndvi.bar.fill} />}
            barSize={20}
            className='ndvi-bar'
          />
          {RenderTrendLine({
            dataKey: 'ndviLine',
            stroke: chartColors.ndvi.line.stroke,
            label: (props) =>
              props.index === data.length - 1 && trendLabel ? (
                <TrendLabel
                  trend={trendLabel}
                  lineColor={chartColors.ndvi.line.stroke}
                  strokeWidth={2}
                  infoPopoverProps={{
                    title: t(`shared.projects.project.conservation.trendLabel.${trendLabel}`),
                    body: t(`shared.projects.project.conservation.explainers.tooltip.trendLabel.${trendLabel}`),
                  }}
                  {...props}
                />
              ) : (
                <></>
              ),
            dotFill: chartColors.ndvi.line.dot,
          })}
          <Chart.XAxis {...xAxisProps} padding={{ left: 40, right: 100 }} className='ndvi-x-axis' />
          <Chart.YAxis domain={[0, 100]} className='ndvi-y-axis'>
            <Chart.YAxisLabel>{t('shared.ncaDetail.details.landMonitoring.ndvi.value')}</Chart.YAxisLabel>
          </Chart.YAxis>
          <Chart.Tooltip cursor={false} content={(props) => <CustomTooltip {...props} />} />
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar trendLabel={trendLabel} />
    </>
  );
};

const CustomLegendBar = ({ trendLabel }: { trendLabel: TrendLabelEnum | null }) => {
  const { t } = useTranslation();

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t(VEGETATION_LABEL),
      color: chartColors.ndvi.bar.fill,
      shape: 'column',
    },
  ];

  if (trendLabel) {
    legendBarItems.push({
      label: t('shared.ncaDetail.details.landMonitoring.ndvi.trend'),
      color: chartColors.ndvi.line.stroke,
      shape: 'line',
    });
  }

  return <ChartLegendBar items={legendBarItems} className='sm:ml-[28px]' data-testid='ndvi-chart-legend' />;
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  if (!active || !payload?.length) return null;

  const linePayload = payload.find((data) => data.dataKey === 'ndviLine');
  const ndviDisplayValue =
    linePayload?.value !== undefined && linePayload?.value !== null
      ? getDisplayNumber(linePayload.value as string, window.navigator.language)
      : t('global.analysis.noData');

  return (
    <Stack direction='col' spacing={1.5} className='rounded-md bg-white-100 px-2 py-1 elevation-1'>
      <Stack spacing={2}>
        <ChartTooltipTextSecondary>{printYear(label)}</ChartTooltipTextSecondary>
        <Stack spacing={8} direction='row'>
          <ChartTooltipTextPrimary>{t(VEGETATION_LABEL)}</ChartTooltipTextPrimary>
          <ChartTooltipTextPrimary>{ndviDisplayValue}</ChartTooltipTextPrimary>
        </Stack>
      </Stack>
    </Stack>
  );
};
